import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

interface PerSystemStats {
  systemId: string;
  total: number;
  last7Days: number;
  today: number;
}

interface SummaryResponse {
  totalAllTime: number;
  totalLast7Days: number;
  totalToday: number;
  perSystem: Record<string, PerSystemStats>;
  daily: { date: string; total: number }[];
}

interface HealthResponse {
  status: string;
  timestamp: string;
  totalRecordings: number;
  systems: Record<string, {
    lastSyncDate: string;
    lastSyncTotal: number;
    lastSync: string | null;
  }>;
}

const SYSTEM_LABELS: Record<string, string> = {
  pulsedial: 'PulseDial',
  vicidial: 'Vicidial',
  dialedin: 'DialedIn (Medicare)',
  '3cx-legacy': '3CX Legacy',
};

const ALL_SYSTEMS = ['pulsedial', 'vicidial', 'dialedin', '3cx-legacy'];

function Dashboard() {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [systemFilter, setSystemFilter] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [dateStats, setDateStats] = useState<{ date: string; total: number; systems: any } | null>(null);
  // Visible error state for the stats fetch — don't silently render an
  // all-zeros dashboard when the request failed.
  const [statsError, setStatsError] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    const params: { systemId?: string } = {};
    if (systemFilter !== 'all') params.systemId = systemFilter;

    setLoading(true);
    setStatsError(false);
    Promise.all([
      api.getDailyStats(params as any).catch(() => { setStatsError(true); return null; }),
      api.getHealth().catch(() => null),
    ]).then(([statsData, healthData]) => {
      setSummary(statsData);
      setHealth(healthData);
    }).finally(() => setLoading(false));
  }, [systemFilter, reloadNonce]);

  // Fetch stats for a specific date
  useEffect(() => {
    if (!selectedDate) { setDateStats(null); return; }
    const params: any = { date: selectedDate };
    if (systemFilter !== 'all') params.systemId = systemFilter;
    api.getStats(params)
      .then((data: any) => setDateStats({ date: selectedDate, total: data.systems ? data.systems.reduce((s: number, sys: any) => s + (sys.total || 0), 0) : (data.total || 0), systems: data.systems || [data] }))
      .catch(() => setDateStats(null));
  }, [selectedDate, systemFilter]);

  if (loading) return <div className="loading">Loading dashboard...</div>;

  const avgPerDay = summary?.daily && summary.daily.length > 0
    ? Math.round(summary.daily.reduce((sum, d) => sum + d.total, 0) / summary.daily.length)
    : 0;

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
          <span style={{ color: '#666', fontSize: '14px' }}>System:</span>
          <select
            value={systemFilter}
            onChange={e => setSystemFilter(e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px',
              background: '#fff',
            }}
          >
            <option value="all">All Systems</option>
            {ALL_SYSTEMS.map(sys => (
              <option key={sys} value={sys}>{SYSTEM_LABELS[sys]}</option>
            ))}
          </select>
          <span style={{ color: '#666', fontSize: '14px', marginLeft: '8px' }}>Date:</span>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #ddd',
              fontSize: '14px',
            }}
          />
          {selectedDate && (
            <button
              type="button"
              onClick={() => setSelectedDate('')}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', background: '#fff', cursor: 'pointer' }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {selectedDate && dateStats && (
        <div style={{
          padding: '16px 20px',
          marginBottom: '16px',
          background: '#e0f7fa',
          borderRadius: '8px',
          border: '1px solid #80deea',
        }}>
          <strong>{selectedDate}:</strong> {dateStats.total} recording{dateStats.total !== 1 ? 's' : ''}
          {dateStats.systems?.length > 1 && (
            <span style={{ marginLeft: '16px', color: '#555' }}>
              ({dateStats.systems.map((s: any) => `${s.systemId || 'unknown'}: ${s.total || 0}`).join(', ')})
            </span>
          )}
        </div>
      )}

      {statsError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 16, background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 8, fontSize: 13, color: '#c62828' }}>
          <span>Couldn't load dashboard stats —</span>
          <button
            type="button"
            onClick={() => setReloadNonce(n => n + 1)}
            style={{ padding: '4px 14px', border: '1px solid #c62828', borderRadius: 6, background: '#fff', color: '#c62828', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Recordings</h3>
          <div className="value">{summary?.totalAllTime || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Today</h3>
          <div className="value">{summary?.totalToday || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Last 7 Days</h3>
          <div className="value">{summary?.totalLast7Days || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Avg / Day (30d)</h3>
          <div className="value">{avgPerDay}</div>
        </div>
      </div>

      {/* System Health */}
      {health && (
        <>
          <h2>System Health</h2>
          <div className="stats-grid">
            {ALL_SYSTEMS.filter(s => s !== '3cx-legacy').map(sys => {
              const h = health.systems?.[sys];
              const lastSync = h?.lastSync ? new Date(h.lastSync) : null;
              const minutesAgo = lastSync ? Math.round((Date.now() - lastSync.getTime()) / 60000) : null;
              const isStale = minutesAgo !== null && minutesAgo > 1440; // >24h
              return (
                <div className="stat-card" key={sys}>
                  <h3>
                    <span className={`system-tag ${sys}`} style={{ fontSize: '12px' }}>
                      {SYSTEM_LABELS[sys]}
                    </span>
                  </h3>
                  <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.6' }}>
                    <div>Last sync: {h?.lastSyncDate === 'never' ? (
                      <span className="badge badge-warning">Never</span>
                    ) : (
                      <strong>{h?.lastSyncDate}</strong>
                    )}</div>
                    <div>Files: <strong>{h?.lastSyncTotal || 0}</strong></div>
                    {isStale && <span className="badge badge-danger">Stale</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {systemFilter === 'all' && (
        <>
          <h2>Per-System Totals</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>System</th>
                <th>Total</th>
                <th>Last 7 Days</th>
                <th>Today</th>
              </tr>
            </thead>
            <tbody>
              {ALL_SYSTEMS.map(sys => {
                const s = summary?.perSystem?.[sys];
                return (
                  <tr key={sys}>
                    <td>
                      <span className={`system-tag ${sys === '3cx-legacy' ? 'legacy' : sys}`}>
                        {SYSTEM_LABELS[sys] || sys}
                      </span>
                    </td>
                    <td>{s?.total || 0}</td>
                    <td>{s?.last7Days || 0}</td>
                    <td>{s?.today || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {summary?.daily && summary.daily.length > 0 && (
        <>
          <h2>Calls Per Day (Last 30 Days)</h2>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
            height: '160px',
            padding: '16px',
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            {summary.daily.map(d => {
              const maxVal = Math.max(...summary.daily.map(x => x.total), 1);
              const height = (d.total / maxVal) * 120;
              return (
                <div
                  key={d.date}
                  title={`${d.date}: ${d.total} calls`}
                  onClick={() => setSelectedDate(d.date)}
                  style={{
                    flex: 1,
                    minWidth: '8px',
                    height: `${Math.max(height, 2)}px`,
                    background: selectedDate === d.date ? '#0097a7' : '#00bcd4',
                    borderRadius: '2px 2px 0 0',
                    cursor: 'pointer',
                    border: selectedDate === d.date ? '1px solid #0d47a1' : 'none',
                  }}
                />
              );
            })}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '4px 16px 0',
            fontSize: '11px',
            color: '#999',
          }}>
            <span>{summary.daily[0]?.date}</span>
            <span>{summary.daily[summary.daily.length - 1]?.date}</span>
          </div>
        </>
      )}
      {/* Storage Flow Info */}
      <div className="setup-card" style={{ marginTop: '24px' }}>
        <h3>How Storage Works</h3>
        <div style={{ fontSize: '14px', lineHeight: '1.8', color: '#555' }}>
          <p><strong>Ingestion:</strong> Phone systems upload recordings via SFTP. Every 5 minutes, files are validated, renamed, and archived on the NAS. Metadata syncs to the dashboard automatically.</p>
          <p><strong>S3 backup:</strong> Every recording is uploaded to S3 immediately after ingestion (dual-write). S3 Standard-IA for 30 days (instant recovery if NAS fails), then Glacier Deep Archive after 120 days (~$1/TB/month).</p>
          <p><strong>NAS retention:</strong> Recordings stay on the NAS for 3 years with instant playback. After 3 years, audio files are removed from the NAS to free space — the S3/Glacier copy is the permanent archive.</p>
          <p><strong>Archived recordings:</strong> Show a "Request from Archive" button instead of instant playback. Retrieval takes 12-48 hours — you'll get an email when it's ready.</p>
          <p><strong>Total retention:</strong> 10 years (Medicare compliance). No auto-deletion. All files are append-only.</p>
          <p><strong>PulseDial flow:</strong> Recordings go to S3 first via the API, then sync to NAS. Twilio recordings are only deleted after confirmed in both S3 and NAS.</p>
        </div>
      </div>

      <StoragePanel />
      <CostsPanel />
    </div>
  );
}

interface CostSummary {
  period: number;
  days: number;
  totalCost: number;
  services: Array<{ name: string; total: number }>;
  daily: Array<{ date: string; total: number; byService?: Record<string, number> }>;
  note: string | null;
}

// Distinct colors for the stacked bar chart. First 6 map to the top services
// by spend; remaining services fold into a single "Other" bucket using OTHER_COLOR.
const SERVICE_COLORS = ['#00bcd4', '#e040fb', '#66bb6a', '#ffa726', '#ab47bc', '#29b6f6'];
const OTHER_COLOR = '#90a4ae';

function DailyCostChart({ data }: { data: CostSummary }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Top 6 services by period total; everything else collapses into "Other".
  const topServices = data.services.slice(0, 6).map(s => s.name);
  const colorFor = (svc: string) => {
    const idx = topServices.indexOf(svc);
    return idx >= 0 ? SERVICE_COLORS[idx] : OTHER_COLOR;
  };

  // Build stack segments per day in a stable order (top services first, then Other).
  const days = data.daily.map(d => {
    const segments: Array<{ name: string; value: number; color: string }> = [];
    let otherTotal = 0;
    const bs = d.byService || {};
    for (const svc of topServices) {
      const v = bs[svc] || 0;
      if (v > 0) segments.push({ name: svc, value: v, color: SERVICE_COLORS[topServices.indexOf(svc)] });
    }
    for (const [svc, v] of Object.entries(bs)) {
      if (!topServices.includes(svc) && v > 0) otherTotal += v;
    }
    if (otherTotal > 0) segments.push({ name: 'Other', value: otherTotal, color: OTHER_COLOR });
    return { date: d.date, total: d.total, segments };
  });

  if (days.length === 0) return null;

  const maxTotal = Math.max(...days.map(d => d.total), 0.01);
  const chartHeight = 180;

  // Legend: only services that have any spend in the visible window
  const seen = new Set<string>();
  days.forEach(d => d.segments.forEach(s => seen.add(s.name)));
  const legend = [
    ...topServices.filter(s => seen.has(s)).map(s => ({ name: s, color: colorFor(s) })),
    ...(seen.has('Other') ? [{ name: 'Other', color: OTHER_COLOR }] : []),
  ];

  const fmt = (n: number) => `$${n.toFixed(2)}`;
  const hover = hoverIdx !== null ? days[hoverIdx] : null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Daily breakdown
      </div>

      <div style={{
        position: 'relative',
        background: '#fafafa',
        border: '1px solid #eee',
        borderRadius: 8,
        padding: '12px 12px 8px',
      }}>
        {/* Bars */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          height: chartHeight,
        }}>
          {days.map((d, i) => {
            const barTotalPx = (d.total / maxTotal) * chartHeight;
            return (
              <div
                key={d.date}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(prev => (prev === i ? null : prev))}
                style={{
                  flex: 1,
                  minWidth: 6,
                  height: chartHeight,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  cursor: 'pointer',
                  opacity: hoverIdx === null || hoverIdx === i ? 1 : 0.55,
                  transition: 'opacity 0.1s',
                }}
              >
                {d.segments.length === 0 ? (
                  <div style={{ height: 2, background: '#e0e0e0', borderRadius: '2px 2px 0 0' }} />
                ) : (
                  <div style={{ height: Math.max(barTotalPx, 2), display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
                    {/* Stack bottom-up by reversing the segments */}
                    {[...d.segments].reverse().map((seg, si) => {
                      const segPx = (seg.value / d.total) * Math.max(barTotalPx, 2);
                      return (
                        <div
                          key={`${seg.name}-${si}`}
                          style={{
                            background: seg.color,
                            height: Math.max(segPx, 0.5),
                            width: '100%',
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* X-axis labels — only ends + middle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 4,
          fontSize: 10,
          color: '#999',
        }}>
          <span>{days[0]?.date}</span>
          {days.length > 2 && <span>{days[Math.floor(days.length / 2)]?.date}</span>}
          <span>{days[days.length - 1]?.date}</span>
        </div>

        {/* Hover tooltip */}
        {hover && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 6,
            padding: '8px 10px',
            fontSize: 11,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minWidth: 200,
            pointerEvents: 'none',
          }}>
            <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>{hover.date}</div>
            <div style={{ color: '#555', marginBottom: 6, fontFamily: 'monospace' }}>{fmt(hover.total)} total</div>
            {hover.segments.length === 0 ? (
              <div style={{ color: '#999' }}>No data</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {hover.segments.map(seg => (
                  <div key={seg.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                    <span style={{ width: 9, height: 9, background: seg.color, borderRadius: 2, flexShrink: 0 }} />
                    <span style={{ flex: 1, color: '#444' }}>{seg.name}</span>
                    <span style={{ fontFamily: 'monospace', color: '#666' }}>{fmt(seg.value)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 14px',
        marginTop: 8,
        fontSize: 11,
        color: '#555',
      }}>
        {legend.map(l => (
          <div key={l.name} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 10, height: 10, background: l.color, borderRadius: 2, display: 'inline-block' }} />
            {l.name}
          </div>
        ))}
      </div>
    </div>
  );
}

function CostsPanel() {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState<7 | 30 | 90 | 365>(30);
  const [data, setData] = useState<CostSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError('');
    api.getCostSummary(period)
      .then(setData)
      .catch((err: any) => setError(err.message || 'Failed to load costs'))
      .finally(() => setLoading(false));
  }, [open, period]);

  const fmt = (n: number) => `$${n.toFixed(2)}`;
  const periodLabel = period === 7 ? 'Last 7 days' : period === 30 ? 'Last 30 days' : period === 90 ? 'Last 90 days' : 'Last year';

  return (
    <div className="setup-card" style={{ marginTop: '24px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, fontWeight: 600, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block', width: 12 }}>▶</span>
        AWS Costs
      </button>
      {open && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {([7, 30, 90, 365] as const).map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                style={{
                  padding: '5px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                  border: '1px solid #ddd',
                  background: period === p ? '#00bcd4' : '#fff',
                  color: period === p ? '#fff' : '#555',
                  fontWeight: period === p ? 600 : 400,
                }}
              >
                {p === 7 ? '7 days' : p === 30 ? '30 days' : p === 90 ? '90 days' : '1 year'}
              </button>
            ))}
          </div>

          {loading && <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>}
          {error && <div style={{ color: '#c62828', fontSize: 13 }}>{error}</div>}

          {!loading && !error && data && (
            <>
              <DailyCostChart data={data} />
              <div style={{ display: 'flex', gap: 24, alignItems: 'baseline', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total ({periodLabel})</div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--text-heading)' }}>{fmt(data.totalCost)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Daily average</div>
                  <div style={{ fontSize: 18, color: '#555' }}>{fmt(data.totalCost / Math.max(data.days, 1))}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Days of data</div>
                  <div style={{ fontSize: 18, color: '#555' }}>{data.days} / {data.period}</div>
                </div>
              </div>

              {data.note && (
                <div style={{ padding: '8px 12px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, fontSize: 12, color: '#795548', marginBottom: 12 }}>
                  {data.note}
                </div>
              )}

              {data.services.length === 0 ? (
                <div style={{ color: '#888', fontSize: 13 }}>No cost data yet. Daily fetcher runs at 4 AM ET; backfill 90 days from the Lambda console if you want history immediately.</div>
              ) : (
                <table className="data-table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th style={{ textAlign: 'right' }}>Cost</th>
                      <th style={{ textAlign: 'right' }}>% of total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.services.map(s => (
                      <tr key={s.name}>
                        <td>{s.name}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(s.total)}</td>
                        <td style={{ textAlign: 'right', color: '#888', fontSize: 12 }}>
                          {data.totalCost > 0 ? `${((s.total / data.totalCost) * 100).toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ marginTop: 12, fontSize: 11, color: '#888' }}>
                Costs are pulled daily from AWS Cost Explorer at 4 AM ET. Yesterday's data is the freshest available — Cost Explorer finalizes overnight.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface StorageClassRow {
  storageType: string;
  label: string;
  bytes: number;
  pricePerGb: number;
  monthlyCost: number;
}

interface StorageBucket {
  name: string;
  label: string;
  classes: StorageClassRow[];
  totalBytes: number;
  totalMonthlyCost: number;
}

interface StorageStatus {
  nas: {
    totalBytes: number;
    usedBytes: number;
    availableBytes: number;
    usedPercent: number;
    measuredAt: string | null;
    bySystem: Record<string, number>;
  } | null;
  buckets: StorageBucket[];
}

function fmtBytes(n: number): string {
  if (!n || n < 1024) return `${n || 0} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let size = n / 1024;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
  return `${size.toFixed(size >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtCost(n: number): string {
  if (!n) return '$0.00';
  if (n < 0.01) return '<$0.01';
  return `$${n.toFixed(2)}`;
}

function StoragePanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<StorageStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true); setError('');
    api.getStorageStatus()
      .then(setData)
      .catch((err: any) => setError(err.message || 'Failed to load storage status'))
      .finally(() => setLoading(false));
  }, [open]);

  const totalMonthlyCost = data?.buckets.reduce((sum, b) => sum + b.totalMonthlyCost, 0) || 0;

  return (
    <div className="setup-card" style={{ marginTop: '24px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, fontWeight: 600, color: 'var(--text-heading)', display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block', width: 12 }}>▶</span>
        Storage Monitor
      </button>
      {open && (
        <div style={{ marginTop: 16 }}>
          {loading && <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>}
          {error && <div style={{ color: '#c62828', fontSize: 13 }}>{error}</div>}

          {!loading && !error && data && (
            <>
              {/* NAS section */}
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#444' }}>NAS (UGREEN DXP4800 Plus)</h4>
                {data.nas ? (() => {
                  const nas = data.nas;
                  const pct = nas.usedPercent;
                  const barColor = pct < 50 ? '#27ae60' : pct < 80 ? '#f39c12' : '#e74c3c';
                  const sysEntries = Object.entries(nas.bySystem)
                    .map(([k, v]) => [k, Number(v) || 0] as [string, number])
                    .sort((a, b) => b[1] - a[1]);
                  return (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontSize: 14, marginBottom: 6 }}>
                        <span style={{ fontWeight: 600 }}>{fmtBytes(nas.usedBytes)}</span>
                        <span style={{ color: '#888' }}>used of {fmtBytes(nas.totalBytes)} ({pct.toFixed(2)}%)</span>
                      </div>
                      <div style={{ height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                        <div style={{ width: `${Math.max(0.5, Math.min(100, pct))}%`, height: '100%', background: barColor, transition: 'width 0.3s' }} />
                      </div>
                      {sysEntries.length > 0 && (
                        <div style={{ fontSize: 12, color: '#666', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 16px', maxWidth: 400 }}>
                          {sysEntries.map(([sys, b]) => (
                            <React.Fragment key={sys}>
                              <span style={{ fontFamily: 'monospace' }}>{sys}</span>
                              <span style={{ textAlign: 'right' }}>{fmtBytes(b)}</span>
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                      {nas.measuredAt && (
                        <div style={{ marginTop: 8, fontSize: 11, color: '#aaa' }}>
                          measured {new Date(nas.measuredAt).toLocaleString()}
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <div style={{ fontSize: 13, color: '#888' }}>
                    No data yet — the NAS-side disk-stats cron may not have run.
                  </div>
                )}
              </div>

              {/* S3 buckets */}
              {data.buckets.map(bucket => (
                <div key={bucket.name} style={{ marginBottom: 20 }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#444' }}>
                    S3: {bucket.label}{' '}
                    <span style={{ fontSize: 12, fontWeight: 'normal', color: '#888', fontFamily: 'monospace' }}>
                      ({bucket.name})
                    </span>
                  </h4>
                  {bucket.classes.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#888' }}>Empty / no metric data yet.</div>
                  ) : (
                    <table className="data-table" style={{ width: 'auto', fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left' }}>Storage Class</th>
                          <th style={{ textAlign: 'right' }}>Size</th>
                          <th style={{ textAlign: 'right' }}>$/GB-month</th>
                          <th style={{ textAlign: 'right' }}>Monthly Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bucket.classes.map(c => (
                          <tr key={c.storageType}>
                            <td>{c.label}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmtBytes(c.bytes)}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>${c.pricePerGb.toFixed(5)}</td>
                            <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmtCost(c.monthlyCost)}</td>
                          </tr>
                        ))}
                        <tr style={{ borderTop: '2px solid #ddd' }}>
                          <td style={{ fontWeight: 600 }}>Total</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtBytes(bucket.totalBytes)}</td>
                          <td></td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtCost(bucket.totalMonthlyCost)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              ))}

              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #eee', fontSize: 14 }}>
                <strong>Total S3 storage cost: {fmtCost(totalMonthlyCost)}/month</strong>
                <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>
                  (storage only — retrieval and request costs shown in AWS Costs panel)
                </span>
              </div>

              <div style={{ marginTop: 12, fontSize: 12, color: '#888', lineHeight: 1.6 }}>
                <strong>Storage class lifecycle:</strong> Standard = recent (&lt;30 days),
                Standard-IA = 30-120 days old, Glacier Deep Archive = 120+ days old.
                Lower classes are cheaper but slower to retrieve.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
